import { useState } from "react";
import { Search, X, Plus, Building2, Globe, Tag, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useCompanySearch, type CompanySearchResult } from "@/hooks/companies/useCompanySearch";
import { useCreateCompany } from "@/hooks/companies/useCompanies";

const INDUSTRY_OPTIONS = [
  { value: "tecnologia", label: "Tecnologia" },
  { value: "saude", label: "Saúde" },
  { value: "educacao", label: "Educação" },
  { value: "financeiro", label: "Financeiro" },
  { value: "varejo", label: "Varejo" },
  { value: "manufatura", label: "Manufatura" },
  { value: "servicos", label: "Serviços" },
  { value: "consultoria", label: "Consultoria" },
  { value: "outros", label: "Outros" },
];

interface CompanyStepProps {
  selectedCompany: CompanySearchResult | null;
  /** A empresa veio herdada do lead (muda só o rótulo do card). */
  inherited: boolean;
  onSelect: (company: CompanySearchResult | null) => void;
}

export function CompanyStep({ selectedCompany, inherited, onSelect }: CompanyStepProps) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const { companies, isLoading, query, setQuery } = useCompanySearch();

  if (selectedCompany) {
    const industryLabel =
      INDUSTRY_OPTIONS.find((o) => o.value === selectedCompany.industry)?.label || selectedCompany.industry;
    return (
      <div
        className={
          inherited
            ? "rounded-lg border border-success/40 bg-success/5 p-3 space-y-1"
            : "rounded-lg border bg-card p-3 space-y-1"
        }
      >
        <div
          className={
            inherited
              ? "flex items-center gap-1.5 text-2xs font-medium text-success uppercase tracking-wide"
              : "flex items-center gap-1.5 text-2xs font-medium text-muted-foreground uppercase tracking-wide"
          }
        >
          <Building2 className="h-3 w-3" />
          {inherited ? "Empresa do lead" : "Empresa vinculada"}
        </div>
        <div className="flex items-center gap-2">
          <div
            className={
              inherited
                ? "h-7 w-7 rounded-md bg-success/10 flex items-center justify-center text-success font-semibold text-xs"
                : "h-7 w-7 rounded-md bg-muted flex items-center justify-center text-foreground font-semibold text-xs"
            }
          >
            {selectedCompany.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{selectedCompany.name}</p>
            <div className="flex items-center gap-3 text-2xs text-muted-foreground">
              {selectedCompany.website && (
                <span className="flex items-center gap-1 truncate">
                  <Globe className="h-3 w-3 flex-shrink-0" />
                  {selectedCompany.website}
                </span>
              )}
              {industryLabel && (
                <span className="flex items-center gap-1">
                  <Tag className="h-3 w-3 flex-shrink-0" />
                  {industryLabel}
                </span>
              )}
            </div>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={() => onSelect(null)}>
            Trocar
          </Button>
        </div>
      </div>
    );
  }

  if (creating) {
    return (
      <QuickCreateCompany
        onCancel={() => setCreating(false)}
        onCreated={(company) => {
          setCreating(false);
          onSelect(company);
        }}
      />
    );
  }

  return (
    <div className="space-y-3">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-start text-left font-normal h-10 text-muted-foreground"
          >
            <Search className="mr-2 h-4 w-4 flex-shrink-0" />
            Buscar empresa por nome...
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Buscar empresa..." value={query} onValueChange={setQuery} />
            <CommandList>
              <CommandEmpty>
                {isLoading
                  ? "Buscando..."
                  : query.length >= 2
                    ? `Nenhuma empresa encontrada para "${query}"`
                    : "Digite para buscar empresas..."}
              </CommandEmpty>
              <CommandGroup heading={query.length < 2 ? "Recentes" : "Resultados"}>
                {companies.map((company) => (
                  <CommandItem
                    key={company.id}
                    value={company.id}
                    onSelect={() => {
                      onSelect(company);
                      setOpen(false);
                      setQuery("");
                    }}
                    className="flex items-center gap-3 py-2.5"
                  >
                    <div className="h-7 w-7 rounded-md bg-muted flex items-center justify-center text-foreground font-semibold text-2xs flex-shrink-0">
                      {company.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{company.name}</p>
                      {company.website && (
                        <p className="text-2xs text-muted-foreground truncate">{company.website}</p>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-2xs text-muted-foreground uppercase tracking-wide">ou</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <Button type="button" variant="outline" className="w-full gap-2" onClick={() => setCreating(true)}>
        <Plus className="h-4 w-4" />
        Criar nova empresa
      </Button>
    </div>
  );
}

function QuickCreateCompany({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (company: CompanySearchResult) => void;
}) {
  const createCompany = useCreateCompany();
  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("");
  const [website, setWebsite] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError("Nome é obrigatório");
      return;
    }
    setError(null);
    try {
      const created = await createCompany.mutateAsync({
        name: name.trim(),
        industry: industry || undefined,
        website: website.trim() || undefined,
      });
      onCreated({
        id: created.id,
        name: created.name,
        website: created.website ?? null,
        industry: created.industry ?? null,
      });
    } catch {
      // toast tratado pelo hook
    }
  };

  return (
    <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-semibold">Nova empresa</h5>
        <Button type="button" variant="ghost" size="icon-sm" onClick={onCancel} aria-label="Cancelar criação">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-2">
        <Label htmlFor="quick-company-name">Nome *</Label>
        <Input
          id="quick-company-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nome da empresa"
          autoFocus
        />
        {error && <p className="text-2xs text-destructive">{error}</p>}
      </div>

      <div className="space-y-2">
        <Label>Setor</Label>
        <SearchableSelect
          value={industry}
          onValueChange={setIndustry}
          placeholder="Selecione o setor"
          options={INDUSTRY_OPTIONS}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="quick-company-website">Website</Label>
        <Input
          id="quick-company-website"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="https://empresa.com"
        />
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="button" onClick={handleCreate} disabled={createCompany.isPending} className="gap-2">
          {createCompany.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Criar e vincular
        </Button>
      </div>
    </div>
  );
}
